<template>
  <div class="bg-white rounded-3xl max-md:rounded-2xl p-5 max-md:p-3">
    <h2 class="text-txprimary font-medium text-lg max-md:text-base leading-tight">
      Выберите формат размещения
    </h2>

    <div class="mt-[23px] max-md:mt-4 flex flex-col gap-4 max-md:gap-2">
      <div
        v-for="(price, index) in sortedPrices"
        :key="price.priceType"
        class="rounded-2xl border border-[#D9E5FF] p-[15px] max-md:p-[11px]"
        :class="index === 0 ? 'bg-bgnew' : 'bg-white'"
      >
        <div
          class="flex items-center justify-between gap-4 max-md:flex-col max-md:items-stretch max-md:gap-3"
        >
          <div class="min-w-0">
            <div
              v-if="index === 0"
              class="w-fit rounded-full px-2.5 py-1 mb-2 text-xs text-white leading-tight"
              :style="{ backgroundColor: accentColor }"
            >
              Популярно
            </div>
            <div class="text-txprimary text-sm font-medium leading-tight">
              {{ getFormatMeta(price).title }}
            </div>
            <p
              v-if="getFormatMeta(price).description"
              class="text-xs text-txprimary/60 leading-normal mt-1"
            >
              {{ getFormatMeta(price).description }}
            </p>
          </div>

          <div class="flex items-center gap-6 max-md:gap-3 shrink-0 max-md:justify-between">
            <div class="text-txprimary text-xl max-md:text-base leading-tight whitespace-nowrap">
              {{ price.price.toLocaleString() }} ₽
            </div>
            <button
              v-if="!findBasketItem(price)"
              @click="addPlaceToBasket(price)"
              class="h-[49px] w-[154px] max-md:h-9 max-md:w-[109px] rounded-2xl max-md:rounded-xl flex items-center justify-center text-white hover:opacity-80 transition-opacity"
              :style="{ backgroundColor: accentColor }"
            >
              <Icon name="fa7-solid:shopping-cart" class="text-[22px] text-white" />
            </button>
            <div
              v-else
              class="h-[49px] w-[154px] max-md:h-9 max-md:w-[109px] rounded-2xl max-md:rounded-xl border border-[#D9E5FF] flex items-center justify-between px-4"
              :class="index === 0 ? 'bg-white' : 'bg-bgnew'"
            >
              <button class="flex items-center justify-center" @click="decreaseQuantity(price)">
                <IMinus class="size-4" :style="{ fill: accentColor }" />
              </button>
              <span class="text-txmain text-sm">{{ findBasketItem(price)?.quantity || 1 }}</span>
              <button class="flex items-center justify-center" @click="increaseQuantity(price)">
                <IPlus class="size-4" :style="{ fill: accentColor }" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { FullPlace, Place, PlacePrice, ReducedPlace } from "~/interfaces/publisher";
  import IPlus from "~/components/UINew/icons/IPlus.vue";
  import IMinus from "~/components/UINew/icons/IMinus.vue";
  import BasketService from "~/services/BasketService";
  import { useBasketStore } from "~/store/BasketModule";
  import { usePlaceModuleStore } from "~/store/PlaceModule";

  const props = withDefaults(defineProps<{ place?: FullPlace | null }>(), { place: null });

  const placeStore = usePlaceModuleStore();
  const { colorOptions } = storeToRefs(placeStore);
  const accentColor = computed(() =>
    props.place?.buttonsColor ? (colorOptions.value[props.place.buttonsColor] ?? "#3176FF") : "#3176FF"
  );

  const basketStore = useBasketStore();
  const { basket } = storeToRefs(basketStore);

  const priceOrder = [
    "post24h",
    "post48h",
    "post72h",
    "post30d",
    "fixedInTop",
    "repost",
    "story",
    "native",
  ];
  const sortedPrices = computed(
    () =>
      props.place?.prices
        ?.slice()
        .sort((a, b) => priceOrder.indexOf(a.priceType) - priceOrder.indexOf(b.priceType)) || []
  );

  const FORMAT_META: Record<string, { title: string; description: string }> = {
    post24h: {
      title: "Пост 24 часа",
      description: "Рекламный пост остаётся в ленте канала 24 часа.",
    },
    post48h: {
      title: "Пост 48 часов",
      description: "Пост остаётся в ленте дольше и подходит для промо с длинным окном заявки.",
    },
    post72h: {
      title: "Пост 72 часа",
      description: "Пост остаётся в ленте канала 72 часа.",
    },
    post30d: {
      title: "Пост 30 дней",
      description: "Пост остаётся в ленте канала 30 дней.",
    },
    fixedInTop: {
      title: "Пост 30 дней",
      description: "Пост остаётся в ленте канала 30 дней.",
    },
    repost: {
      title: "Репост",
      description: "Репост записи, 24 часа в ленте канала.",
    },
    story: {
      title: "Сторис",
      description: "Публикация в сторис канала на 24 часа.",
    },
    native: {
      title: "Натив",
      description: "Нативное размещение в контенте канала.",
    },
  };
  const getFormatMeta = (price: PlacePrice) =>
    FORMAT_META[price.priceType] || { title: price.name || price.priceType, description: "" };

  const findBasketItem = (price: PlacePrice) =>
    basket.value.find(
      (item) => item.id === props.place?.id && item.format?.priceType === price.priceType
    );

  const addPlaceToBasket = (price: PlacePrice) => {
    const place = props.place;
    if (!place) return;
    const existingPlace = findBasketItem(price);
    if (existingPlace) {
      existingPlace.quantity = (existingPlace.quantity || 1) + 1;
      return;
    }

    // Создаем полностью независимую копию объекта для корзины
    const basketItem = JSON.parse(
      JSON.stringify({
        id: place.id,
        name: place.name,
        url: place.url,
        avatar: place.avatar,
        format: price,
        quantity: 1,
        // Копируем только необходимые поля, чтобы избежать циклических ссылок
        categories: place.categories,
        subscribersStatistic: place.subscribersStatistic,
        prices: place.prices,
        viewsStatistic: place.viewsStatistic,
        er: place.er,
      })
    );

    BasketService.addPlace(
      basketItem as Place & { format: PlacePrice | null; subscribersStatistic: { count: number } }
    );
    basket.value.push(basketItem as any);
  };

  const removePlace = (price: PlacePrice) => {
    basket.value = basket.value.filter(
      (p: ReducedPlace) => !(p.id === props.place?.id && p.format?.priceType === price.priceType)
    );
    if (props.place) BasketService.removePlace(props.place.id, price);
  };

  const increaseQuantity = (price: PlacePrice) => {
    const basketItem = findBasketItem(price);
    if (basketItem && basketItem.quantity && basketItem.quantity < 10) {
      basketItem.quantity++;
    }
  };

  const decreaseQuantity = (price: PlacePrice) => {
    const basketItem = findBasketItem(price);
    if (basketItem && basketItem.quantity && basketItem.quantity > 1) {
      basketItem.quantity--;
    } else {
      removePlace(price);
    }
  };
</script>
